import { Box, CircularProgress } from '@mui/material';
import { Grid } from '@mui/material';
import React, { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MfaRequiredError,
    type MFAMethod,
    TooManyRequestsError,
    UnauthorizedError,
} from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { StringParam, useQueryParam } from 'src/helpers/queryParam';
import { Button } from 'src/lib/components/Button';
import { TextField } from 'src/lib/components/fields/TextField';
import { Typography } from 'src/lib/components/Typography';
import {
    buildMfaChallengeVerifyRequest,
    getInitialMfaMethod,
    type PendingMFAChallenge,
    toPendingMfaChallenge,
} from '../components/loginMfaChallenge';
import { InvalidMagicLink } from './InvalidMagicLink';
import { buildPostMagicLinkUrl } from './magicLinkUrl';

const ConsumingMagicLink = () => {
    const { t } = useTranslation('auth.magicLink');
    return (
        <Grid
            container
            alignItems="center"
            justifyContent="center"
            direction="column"
            style={{ minHeight: '100vh' }}
        >
            <CircularProgress size={100} />
            <Box my={4}>{t('processingLoginLink')}</Box>
        </Grid>
    );
};

export const MagicLinkConsumer = ({ children }: { children: React.ReactNode }) => {
    const apiClient = useAPI();

    const [loginFailed, setLoginFailed] = useState(false);
    const [pendingMfaChallenge, setPendingMfaChallenge] = useState<PendingMFAChallenge | null>(
        null,
    );
    const [mfaMethod, setMfaMethod] = useState<MFAMethod>('totp');
    const [mfaCode, setMfaCode] = useState('');
    const [mfaError, setMfaError] = useState<string | null>(null);
    const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
    const [username] = useQueryParam('username', StringParam);
    const [token] = useQueryParam('magic_link_token', StringParam);
    const { t: tLogin } = useTranslation('auth.login');
    const { t: tValidation } = useTranslation('validation');

    const redirectAfterMagicLink = useCallback(() => {
        window.location.replace(
            buildPostMagicLinkUrl({
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
            }),
        );
    }, []);

    const requestMagicLinkMfaChallenge = useCallback(async () => {
        if (!token) {
            return false;
        }
        try {
            await apiClient.post('/magic-link-login/', { token });
            redirectAfterMagicLink();
            return true;
        } catch (err: any) {
            const challenge =
                err instanceof MfaRequiredError ? toPendingMfaChallenge(err, username || '') : null;
            if (!challenge) {
                return false;
            }
            setMfaMethod(getInitialMfaMethod(challenge.methods));
            setMfaCode('');
            setMfaError(null);
            setPendingMfaChallenge(challenge);
            return true;
        }
    }, [apiClient, redirectAfterMagicLink, token, username]);

    useEffect(() => {
        void (async () => {
            if (!token) {
                return;
            }
            const handled = await requestMagicLinkMfaChallenge();
            if (!handled) {
                setLoginFailed(true);
            }
        })();
    }, [requestMagicLinkMfaChallenge, token]);

    const verifyMfaChallenge = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!pendingMfaChallenge) {
            return;
        }
        if (!mfaCode.trim()) {
            setMfaError(tValidation('required'));
            return;
        }
        setIsVerifyingMfa(true);
        setMfaError(null);
        try {
            await apiClient.post(
                '/auth/mfa/challenge/verify/',
                buildMfaChallengeVerifyRequest({
                    challenge: pendingMfaChallenge,
                    code: mfaCode,
                    method: mfaMethod,
                }),
            );
            redirectAfterMagicLink();
        } catch (err: any) {
            if (err instanceof TooManyRequestsError) {
                setMfaError(err.message || tLogin('mfaTooManyAttempts'));
                return;
            }
            if (err instanceof UnauthorizedError) {
                const refreshedChallenge = await requestMagicLinkMfaChallenge();
                if (!refreshedChallenge) {
                    setLoginFailed(true);
                    return;
                }
                setMfaError(tLogin('mfaInvalidCode'));
                return;
            }
            setLoginFailed(true);
        } finally {
            setIsVerifyingMfa(false);
        }
    };

    if (pendingMfaChallenge) {
        const canUseSms = pendingMfaChallenge.methods.includes('sms');
        const canUseTotp = pendingMfaChallenge.methods.includes('totp');
        const canUseRecoveryCode = pendingMfaChallenge.methods.includes('recovery_code');
        const isSms = mfaMethod === 'sms';
        const isRecoveryCode = mfaMethod === 'recovery_code';
        const mfaMethodSwitchOptions: { method: MFAMethod; label: string }[] = [];
        if (canUseSms && !isSms) {
            mfaMethodSwitchOptions.push({ method: 'sms', label: tLogin('mfaUseSmsCode') });
        }
        if (canUseTotp && mfaMethod !== 'totp') {
            mfaMethodSwitchOptions.push({
                method: 'totp',
                label: tLogin('mfaUseAuthenticatorCode'),
            });
        }
        if (canUseRecoveryCode && !isRecoveryCode) {
            mfaMethodSwitchOptions.push({
                method: 'recovery_code',
                label: tLogin('mfaUseRecoveryCode'),
            });
        }

        return (
            <Grid
                container
                alignItems="center"
                justifyContent="center"
                direction="column"
                style={{ minHeight: '100vh' }}
            >
                <Box width="100%" maxWidth={420}>
                    <form onSubmit={verifyMfaChallenge} autoComplete="on">
                        <Box marginBottom={3}>
                            <Typography role="heading2" component="h1">
                                {tLogin('mfaTitle')}
                            </Typography>
                        </Box>
                        <Box marginBottom={3}>
                            <Typography role="body" color="subdued">
                                {isSms
                                    ? tLogin('mfaSmsDescription', {
                                          username: pendingMfaChallenge.username,
                                      })
                                    : tLogin('mfaDescription', {
                                          username: pendingMfaChallenge.username,
                                      })}
                            </Typography>
                        </Box>
                        <Box marginBottom={4}>
                            <TextField
                                label={
                                    isRecoveryCode
                                        ? tLogin('mfaRecoveryCodeLabel')
                                        : isSms
                                          ? tLogin('mfaSmsCodeLabel')
                                          : tLogin('mfaCodeLabel')
                                }
                                name="mfa_code"
                                autoComplete="one-time-code"
                                value={mfaCode}
                                onChange={(event) => {
                                    setMfaCode(event.target.value);
                                    setMfaError(null);
                                }}
                                error={mfaError ?? undefined}
                                autoFocus
                            />
                        </Box>
                        <Box marginBottom={2}>
                            <Button
                                type="submit"
                                kind="filled"
                                color="primary"
                                fullWidth
                                disabled={isVerifyingMfa}
                            >
                                {tLogin('mfaVerifyButton')}
                            </Button>
                        </Box>
                        {mfaMethodSwitchOptions.map((option) => (
                            <Box key={option.method} marginBottom={2}>
                                <Button
                                    type="button"
                                    kind="text"
                                    color="secondary"
                                    fullWidth
                                    disabled={isVerifyingMfa}
                                    onClick={() => {
                                        setMfaMethod(option.method);
                                        setMfaCode('');
                                        setMfaError(null);
                                    }}
                                >
                                    {option.label}
                                </Button>
                            </Box>
                        ))}
                    </form>
                </Box>
            </Grid>
        );
    }

    if (token) {
        return loginFailed ? (
            <InvalidMagicLink initialUsername={username || ''} />
        ) : (
            <ConsumingMagicLink />
        );
    }

    return <>{children}</>;
};
